/**
 * Guided breathing patterns.
 *
 * Cyclic sighing is the default because it has the best evidence for fast
 * calming (Balban et al., 2023): two inhales through the nose, then a long
 * exhale through the mouth. What seems to matter is the LONG EXHALE and a
 * slow overall pace (~6 breaths a minute). Box breathing is offered because
 * many students already know it.
 */
export type BreathingPatternId = 'cyclic' | 'box';

export interface BreathStep {
  cue: string;
  seconds: number;
  /** Circle size this step eases TO (0–1). */
  scale: number;
}

export interface BreathingPattern {
  id: BreathingPatternId;
  label: string;
  /** Why pick this one, in a line. */
  why: string;
  description: string;
  steps: readonly BreathStep[];
}

export const BREATHING_PATTERNS: Record<BreathingPatternId, BreathingPattern> =
  {
    cyclic: {
      id: 'cyclic',
      label: 'Cyclic sigh',
      why: 'The fastest way to calm down.',
      description:
        'Two breaths in through your nose, then one long, slow breath out.',
      steps: [
        { cue: 'Breathe in', seconds: 2.5, scale: 0.85 },
        { cue: 'A little more', seconds: 1, scale: 1 },
        { cue: 'Long, slow breath out', seconds: 6.5, scale: 0.45 },
      ],
    },
    box: {
      id: 'box',
      label: 'Box breathing',
      why: 'Steadies you before a test.',
      description: 'In, hold, out, hold - four counts each.',
      steps: [
        { cue: 'Breathe in', seconds: 4, scale: 1 },
        { cue: 'Hold', seconds: 4, scale: 1 },
        { cue: 'Breathe out', seconds: 4, scale: 0.45 },
        { cue: 'Hold', seconds: 4, scale: 0.45 },
      ],
    },
  };

/** The circle's size at rest, before a session starts and after it ends. */
export const REST_SCALE = 0.45;

export function cycleSeconds(pattern: BreathingPattern): number {
  return pattern.steps.reduce((sum, s) => sum + s.seconds, 0);
}

export interface BreathPosition {
  stepIndex: number;
  step: BreathStep;
  /** 0–1 through the current step. */
  stepProgress: number;
  /** Seconds left in the current step. */
  stepRemaining: number;
}

/** Where in the pattern a session is, `elapsedMs` after it started. */
export function breathAt(
  pattern: BreathingPattern,
  elapsedMs: number
): BreathPosition {
  const cycle = cycleSeconds(pattern);
  let t = (Math.max(0, elapsedMs) / 1000) % cycle;
  for (let i = 0; i < pattern.steps.length; i++) {
    const step = pattern.steps[i];
    if (t < step.seconds) {
      return {
        stepIndex: i,
        step,
        stepProgress: t / step.seconds,
        stepRemaining: step.seconds - t,
      };
    }
    t -= step.seconds;
  }
  const last = pattern.steps.length - 1;
  return {
    stepIndex: last,
    step: pattern.steps[last],
    stepProgress: 1,
    stepRemaining: 0,
  };
}

/** Breaths in a session of `totalMs`, counting a final partial one. */
export function breathsIn(pattern: BreathingPattern, totalMs: number): number {
  return Math.ceil(totalMs / (cycleSeconds(pattern) * 1000));
}
